import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkIcon from '@mui/icons-material/Link';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { toast } from 'react-toastify';

const getSiteOrigin = () => {
  if (typeof window === 'undefined') return '';
  return window.location?.origin || '';
};

/**
 * 社群分享按鈕元件
 */
const SocialShare = ({ title, description, url, image, product = null }) => {
  const origin = getSiteOrigin();
  // `url` is expected to be a path like `/products/1` but tolerate full URLs.
  const shareUrl = (() => {
    try {
      return new URL(url, origin || 'http://localhost').toString();
    } catch {
      return `${origin}${url || ''}`;
    }
  })();
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  // 分享連結
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    line: `https://social-plugins.line.me/lineit/share?url=${encodedUrl}`,
    messenger: `fb-messenger://share/?link=${encodedUrl}`,
  };

  // 複製連結
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('連結已複製！');

      // GTM 事件
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'share',
          method: 'copy_link',
          content_type: 'product',
          item_id: product?.sku
        });
      }
    } catch (err) {
      toast.error('複製失敗');
    }
  };

  // 開啟分享視窗
  const openShare = (platform, link) => {
    // GTM 事件
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'share',
        method: platform,
        content_type: 'product',
        item_id: product?.sku
      });
    }

    // Facebook Pixel 事件
    if (window.fbq) {
      window.fbq('track', 'Share', {
        content_type: 'product',
        content_ids: [product?.sku]
      });
    }

    window.open(link, '_blank', 'width=600,height=400');
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        分享：
      </Typography>

      {/* Facebook */}
      <Tooltip title="分享到 Facebook">
        <IconButton
          size="small"
          onClick={() => openShare('facebook', shareLinks.facebook)}
          sx={{ color: '#1877F2' }}
        >
          <FacebookIcon />
        </IconButton>
      </Tooltip>

      {/* LINE */}
      <Tooltip title="分享到 LINE">
        <IconButton
          size="small"
          onClick={() => openShare('line', shareLinks.line)}
          sx={{ color: '#00B900' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.063-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
          </svg>
        </IconButton>
      </Tooltip>

      {/* Twitter */}
      <Tooltip title="分享到 Twitter">
        <IconButton
          size="small"
          onClick={() => openShare('twitter', shareLinks.twitter)}
          sx={{ color: '#1DA1F2' }}
        >
          <TwitterIcon />
        </IconButton>
      </Tooltip>

      {/* TikTok */}
      <Tooltip title="複製連結分享到 TikTok">
        <IconButton
          size="small"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(`${title} ${shareUrl}`);
              toast.success('已複製！可貼到 TikTok 分享');

              // GTM 事件
              if (window.dataLayer) {
                window.dataLayer.push({
                  event: 'share',
                  method: 'tiktok',
                  content_type: 'product',
                  item_id: product?.sku
                });
              }

              // TikTok Pixel 事件
              if (window.ttq) {
                window.ttq.track('ClickButton', {
                  content_id: product?.sku,
                  content_type: 'product',
                  description: 'share_to_tiktok'
                });
              }
            } catch (err) {
              toast.error('複製失敗');
            }
          }}
          sx={{ color: '#000' }}
        >
          <MusicNoteIcon />
        </IconButton>
      </Tooltip>

      {/* Copy Link */}
      <Tooltip title="複製連結">
        <IconButton
          size="small"
          onClick={copyLink}
          sx={{ color: '#666' }}
        >
          <LinkIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default SocialShare;
